import { create } from 'zustand';
import { Api } from '../api/api';

interface ProfileState {
  firstName: string;
  lastName: string;
  nickname: string;
  email: string;
  vkLink: string;
  telegramAccount: string;
  phoneNumber: string;
  showVk: boolean;
  showTelegram: boolean;
  showFirstName: boolean;
  showLastName: boolean;
  showUserName: boolean;
  accessStockroom: boolean;

  // Состояния загрузки для разных операций
  isFetching: boolean;
  isUpdating: boolean;
  isUpdatingVision: boolean;
  isUpdatingPassword: boolean;

  // Действия
  fetchProfile: () => Promise<void>;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;
  setNickname: (nickname: string) => void;
  setEmail: (email: string) => void;
  setVkLink: (vkLink: string) => void;
  setTelegramAccount: (account: string) => void;
  setPhoneNumber: (number: string) => void;
  setShowVk: (visibility: boolean) => void;
  setShowTelegram: (visibility: boolean) => void;
  setShowFirstName: (visibility: boolean) => void;
  setShowLastName: (visibility: boolean) => void;
  setShowUserName: (visibility: boolean) => void;
  updateProfile: () => Promise<void>;
  updateVisionSettings: () => Promise<void>;
  updatePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  resetProfileState: () => void;

  // Вспомогательные геттеры для обратной совместимости
  get isLoading(): boolean;
}

// Начальное состояние
const initialState = {
  firstName: '',
  lastName: '',
  nickname: '',
  email: '',
  vkLink: '',
  telegramAccount: '',
  phoneNumber: '',
  showVk: false,
  showTelegram: false,
  showFirstName: false,
  showLastName: false,
  showUserName: false,
  accessStockroom: false,
  isFetching: false,
  isUpdating: false,
  isUpdatingVision: false,
  isUpdatingPassword: false,
};

export const useProfileStore = create<ProfileState>()((set, get) => ({
  ...initialState,

  fetchProfile: async () => {
    set({ isFetching: true });

    try {
      const api = new Api();
      const userData = await api.getUserInfo();

      set({
        firstName: userData.first_name || userData.user_name || '',
        lastName: userData.last_name || '',
        nickname: userData.user_name || '',
        email: userData.email || '',
        vkLink: userData.vk_account || '',
        telegramAccount: userData.telegram_account || '',
        phoneNumber: userData.phone_number || '',
        // Поддержка как числовых значений 0/1, так и булевых true/false
        showVk: userData.show_vk === 1 || userData.show_vk === true,
        showTelegram: userData.show_telegram === 1 || userData.show_telegram === true,
        showFirstName: userData.show_first_name === 1 || userData.show_first_name === true,
        showLastName: userData.show_last_name === 1 || userData.show_last_name === true,
        showUserName: userData.show_user_name === 1 || userData.show_user_name === true,
        accessStockroom: userData.access_stockroom || false,
        isFetching: false
      });

      return Promise.resolve();
    } catch (error) {
      set({ isFetching: false });
      return Promise.reject(error);
    }
  },

  setFirstName: (firstName) => set({ firstName }),
  setLastName: (lastName) => set({ lastName }),
  setNickname: (nickname) => set({ nickname }),
  setEmail: (email) => set({ email }),
  setVkLink: (vkLink) => set({ vkLink }),
  setTelegramAccount: (telegramAccount) => set({ telegramAccount }),
  setPhoneNumber: (phoneNumber) => set({ phoneNumber }),
  setShowVk: (showVk) => set({ showVk }),
  setShowTelegram: (showTelegram) => set({ showTelegram }),
  setShowFirstName: (showFirstName) => set({ showFirstName }),
  setShowLastName: (showLastName) => set({ showLastName }),
  setShowUserName: (showUserName) => set({ showUserName }),

  updateProfile: async () => {
    set({ isUpdating: true });

    try {
      const {
        firstName,
        lastName,
        nickname,
        email,
        vkLink,
        telegramAccount,
        phoneNumber
      } = get();
      const api = new Api();

      const userData = {
        user_name: nickname,
        first_name: firstName,
        last_name: lastName,
        email,
        vk_account: vkLink,
        telegram_account: telegramAccount,
        phone_number: phoneNumber
      };

      const result = await api.updateUserInfo(userData);
      set({ isUpdating: false });

      return result;
    } catch (error) {
      set({ isUpdating: false });
      throw error;
    }
  },

  updateVisionSettings: async () => {
    set({ isUpdatingVision: true });

    try {
      const {
        showVk,
        showTelegram,
        showFirstName,
        showLastName,
        showUserName
      } = get();
      const api = new Api();

      const visionData = {
        show_vk: showVk,
        show_telegram: showTelegram,
        show_first_name: showFirstName,
        show_last_name: showLastName,
        show_user_name: showUserName
      };

      const result = await api.updateVisionSettings(visionData);
      set({ isUpdatingVision: false });

      return result;
    } catch (error) {
      set({ isUpdatingVision: false });
      throw error;
    }
  },

  // updatePrivacySettings is removed as its functionality is merged into updateProfile

  updatePassword: async (oldPassword, newPassword) => {
    set({ isUpdatingPassword: true });

    try {
      const api = new Api();
      const result = await api.updateUserPassword({
        old_password: oldPassword,
        new_password: newPassword
      });

      set({ isUpdatingPassword: false });

      return result;
    } catch (error) {
      set({ isUpdatingPassword: false });
      throw error;
    }
  },

  resetProfileState: () => set(initialState),

  // Геттер для обратной совместимости
  get isLoading() {
    const state = get();
    return state.isFetching || state.isUpdating || state.isUpdatingVision || state.isUpdatingPassword;
  }
}));